/**
 * Monetization service for handling wallet, hints, ads, and purchases.
 */

import { apiRequest } from './apiBase';
import { t, formatNumber } from '../i18n';

// ============ Wallet ============

/**
 * Get the current user's wallet (hint balance and ad status)
 */
export async function getWallet() {
    return apiRequest('/wallet/');
}

/**
 * Get hint status for the current game
 */
export async function getHintStatus() {
    return apiRequest('/wallet/hint-status/');
}

/**
 * Use a hint for the current game
 */
export async function useHint() {
    return apiRequest('/wallet/use-hint/', {
        method: 'POST',
    });
}

// ============ Ads ============

/**
 * Check if the user should see ads
 */
export async function getAdsStatus() {
    return apiRequest('/ads/status/');
}

/**
 * Request a single-use challenge token from the server before showing a
 * web rewarded ad. The server binds it to the player and verifies the user
 * waited at least a few seconds before redeeming it.
 */
export async function getAdChallenge() {
    return apiRequest('/ads/challenge/', { method: 'POST' });
}

/**
 * Grant hint reward after watching an ad. For web rewarded ads, pass the
 * challenge token returned by getAdChallenge() so the server can validate.
 */
export async function grantAdReward(adType = 'rewarded_video', adUnitId = '', challenge = null) {
    const body = { adType, adUnitId };
    if (challenge) body.challenge = challenge;
    return apiRequest('/ads/reward/', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

// ============ Payments - Stripe ============

/**
 * Create a Stripe PaymentIntent
 */
export async function createPaymentIntent(purchaseType) {
    return apiRequest('/payments/stripe/create-intent/', {
        method: 'POST',
        body: JSON.stringify({ purchaseType }),
    });
}

/**
 * Confirm a Stripe payment
 */
export async function confirmPayment(paymentIntentId, purchaseType) {
    return apiRequest('/payments/stripe/confirm/', {
        method: 'POST',
        body: JSON.stringify({ paymentIntentId, purchaseType }),
    });
}

// ============ Payments - In-App Purchase ============

/**
 * Verify an in-app purchase receipt
 */
export async function verifyIAP(platform, productId, receiptData, transactionId) {
    return apiRequest('/payments/iap/verify/', {
        method: 'POST',
        body: JSON.stringify({
            platform,
            productId,
            receiptData,
            transactionId,
        }),
    });
}

/**
 * Restore previous in-app purchases
 */
export async function restorePurchases(platform, receipts = []) {
    return apiRequest('/payments/iap/restore/', {
        method: 'POST',
        body: JSON.stringify({ platform, receipts }),
    });
}

// ============ Purchase Configuration ============

export function formatPrice(amount) {
    return formatNumber(amount, { style: 'currency', currency: 'EUR' });
}

// Names, badges and prices are getters so they follow the active UI language.
function product(id, productId, priceAmount, extra = {}, { badge, description } = {}) {
    return {
        id,
        productId,
        hints: 0,
        priceAmount,
        ...extra,
        get name() { return t(`store.products.${id}.name`); },
        get price() { return formatPrice(priceAmount); },
        get badge() { return badge?.(); },
        get description() { return description?.(); },
    };
}

export const PURCHASE_OPTIONS = {
    hints_10: product('hints_10', 'com.wordlabyrinth.hints10', 0.99, { hints: 10 }),
    hints_25: product('hints_25', 'com.wordlabyrinth.hints25', 1.99, { hints: 25 }, { badge: () => t('store.badges.popular') }),
    hints_50: product('hints_50', 'com.wordlabyrinth.hints50', 2.99, { hints: 50 }, { badge: () => t('store.badges.bestValue') }),
    remove_ads: product('remove_ads', 'com.wordlabyrinth.removeads', 2.99, {},
        { description: () => t('store.products.remove_ads.description') }),
    streak_freeze_1: product('streak_freeze_1', 'com.wordlabyrinth.freeze1', 0.99, { freezes: 1 }),
    streak_freeze_3: product('streak_freeze_3', 'com.wordlabyrinth.freeze3', 1.99, { freezes: 3 },
        { badge: () => t('store.badges.save', { percent: formatNumber(0.34, { style: 'percent' }) }) }),
    streak_recovery: product('streak_recovery', 'com.wordlabyrinth.streakrecovery', 1.99),
};

// ============ Platform Detection ============

/**
 * Check if running on native mobile (Capacitor)
 */
export function isNativeMobile() {
    return window.Capacitor?.isNativePlatform?.() || false;
}

/**
 * Get the current platform
 */
export function getPlatform() {
    if (!isNativeMobile()) {
        return 'web';
    }
    const platform = window.Capacitor?.getPlatform?.();
    if (platform === 'ios') return 'apple';
    if (platform === 'android') return 'google';
    return 'web';
}
